import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useContext, useMemo } from "react";
import { ToastContext, ToastTypeEnum } from "context/toastContext";
import {
  CreateExaminationInput,
  Examination_Type,
  useAddCompanyCandidatesToExaminationMutation,
  useCreateExaminationMutation,
} from "@generated/graphql";
import qs from "qs";
import { getTestingPath } from "helpers/index";
import { CANDIDATE_IDS_QUERY_NAME } from "constants/global";
import { parseApolloError } from "helpers/errorsParser";

type SearchParams = {
  type?: Examination_Type;
  [CANDIDATE_IDS_QUERY_NAME]?: string;
};

const useCreateExamination = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useContext(ToastContext);
  const [searchParams] = useSearchParams();

  const params = useMemo(() => {
    return qs.parse(searchParams.toString()) as SearchParams;
  }, [searchParams]);
  const searchExaminationType = params.type;
  const companyCandidateIdsString = params[CANDIDATE_IDS_QUERY_NAME];

  const toggleToastOnError = (text?: string) => addToast({ type: ToastTypeEnum.ERROR, text });
  const toggleToastOnSuccess = (text?: string) => addToast({ type: ToastTypeEnum.SUCCESS, text });

  const [createExamination, { loading: createExaminationLoading }] = useCreateExaminationMutation();
  const [addCompanyCandidatesToExamination] = useAddCompanyCandidatesToExaminationMutation();

  const addCandidates = (examinationId: string, companyCandidateIds: string[], onSuccess?: () => void) => {
    addCompanyCandidatesToExamination({
      variables: {
        input: {
          examinationId,
          companyCandidateIds,
        },
      },
    })
      .then(() => {
        addToast({ type: ToastTypeEnum.SUCCESS });
        if (onSuccess) onSuccess();
      })
      .catch((e) => parseApolloError(e, { addToast }));
  };

  const onCreateExamination = (data: Omit<CreateExaminationInput, "type">) => {
    const isGroup = location.pathname.includes("group");
    const isPersonal = location.pathname.includes("personal");
    if ((isGroup && searchExaminationType) || isPersonal) {
      createExamination({
        variables: {
          input: {
            ...data,
            type: searchExaminationType || Examination_Type.Personal,
            // TODO || Examination_Type.Personal как временное ок, но вообще небезопсно
            // хотя бы потому, что если убрать query параметр для группового, то страница открывается,
            // но неизвестно в каком типе нужно создать
          },
        },
      })
        .then((res) => {
          const createdId = isPersonal ? res.data?.createExamination.trials?.[0]?.id : res.data?.createExamination.id;
          if (createdId) {
            if (isPersonal) navigate(getTestingPath("trial", createdId), { replace: true });
            else {
              const navigateToView = () => {
                navigate(getTestingPath("examination", createdId), { replace: true });
              };

              if (companyCandidateIdsString) {
                addCandidates(createdId, companyCandidateIdsString.split(","), navigateToView);
              } else {
                navigateToView();
              }
            }

            toggleToastOnSuccess();
          } else {
            toggleToastOnError();
          }
        })
        .catch((e) => parseApolloError(e, { addToast }));
    } else {
      if (isGroup && !searchExaminationType)
        console.error("group examination:", "отсутствует searchExaminationType", isGroup, searchExaminationType);
      if (isPersonal) {
        console.error("Ошибка создания персоанльного тестрования");
      }
      toggleToastOnError("Ошибка создания тестирования");
    }
  };

  return { createExaminationLoading, onCreateExamination };
};

export default useCreateExamination;
